import type { StateCreator } from 'zustand';
import { debounce } from 'shared/utils/debounce';
import type { Message } from 'shared/domain/messages/message';
import type { ContractEventMeta } from 'shared/channels/contract';
import { showErrorToastWithMessage } from 'services/toaster';
import { directoryClient } from 'shared/domain/directory/directoryClient';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import type { DocumentationStoreSlices } from '../types';
import {
  classifyDirectoryMessage,
  classifyDocumentationMessage,
  getDocumentationErrorMessageId,
  getUploadedDocumentation,
  isRemovalChange,
  resolveDocumentationRefresh,
} from './model';
import type { ChannelUpdatesSlice } from './types';

export * from './types';

const REFRESH_DEBOUNCE_MS = 500;

type ChannelUpdatesSliceCreator = StateCreator<DocumentationStoreSlices, [], [], ChannelUpdatesSlice>;

export const createChannelUpdatesSlice: ChannelUpdatesSliceCreator = (_set, get) => {
  const confirmPendingRemovals = (): void => {
    const { removedRowIds, confirmRowsRemoval } = get();
    if (removedRowIds.size > 0) {
      confirmRowsRemoval([...removedRowIds]);
    }
  };

  const refreshAfterDocumentationChange = debounce((): void => {
    const state = get();
    const { directoryId, restoreDirectory } = resolveDocumentationRefresh(state);
    if (restoreDirectory) {
      void state.navigateToDirectory(directoryId);
      return;
    }
    void state.fetchDirectoryContent(directoryId, { suppressLoadingIndicators: true });
  }, REFRESH_DEBOUNCE_MS);

  const refreshAfterDirectoryChange = debounce(async (): Promise<void> => {
    await get().fetchAllDirectories();
    // Searched directories are matched against all directories.
    get().scheduleSearchResultsRefresh();
    await get().fetchDirectoryContent(get().currentDirectory, {
      suppressLoadingIndicators: true,
    });
  }, REFRESH_DEBOUNCE_MS);

  const refreshAfterUpload = debounce((): void => {
    void get().fetchDirectoryContent(get().currentDirectory, {
      suppressLoadingIndicators: true,
    });
  }, REFRESH_DEBOUNCE_MS);

  const handleDocumentationChannelMessage = (event: Message & ContractEventMeta): void => {
    const errorMessageId = getDocumentationErrorMessageId(event);
    if (errorMessageId) {
      showErrorToastWithMessage(errorMessageId);
    }
    const uploaded = getUploadedDocumentation(event);
    if (uploaded) {
      get().handleDocumentationUploaded(uploaded);
      return;
    }
    const kind = classifyDocumentationMessage(event);
    if (kind) {
      get().handleDocumentationChange(kind);
    }
  };

  const handleDirectoryChannelMessage = (event: Message): void => {
    const kind = classifyDirectoryMessage(event);
    if (kind) {
      get().handleDirectoryChange(kind);
    }
  };

  return {
    subscribeToChannelMessages: (): (() => void) => {
      const unsubscribeFromDocumentations = documentationClient.onEvent(
        handleDocumentationChannelMessage
      );
      const unsubscribeFromDirectories = directoryClient.onEvent(handleDirectoryChannelMessage);
      return () => {
        unsubscribeFromDocumentations();
        unsubscribeFromDirectories();
      };
    },

    handleDocumentationChange: (kind): void => {
      if (isRemovalChange(kind)) {
        // confirmRowsRemoval also removes items from source data - no refetch,
        // which preserves the scroll position.
        confirmPendingRemovals();
      } else {
        refreshAfterDocumentationChange();
      }
      // Directory refreshes do not rebuild search results (and archived items
      // would come back on the next re-filter) - refresh them separately.
      get().scheduleSearchResultsRefresh();
    },

    handleDirectoryChange: (kind): void => {
      if (isRemovalChange(kind)) {
        confirmPendingRemovals();
        // Still refresh the directory tree (breadcrumbs, navigation) and search.
        void get()
          .fetchAllDirectories()
          .then(() => get().scheduleSearchResultsRefresh());
        return;
      }
      refreshAfterDirectoryChange();
    },

    handleDocumentationUploaded: ({ localId, _id, versionId }): void => {
      if (!versionId) {
        return;
      }
      get().updateFileAfterUpload(localId, _id, versionId);
      // One refresh for a batch of uploaded files.
      refreshAfterUpload();
    },

    refreshCurrentDirectory: (): void => {
      void refreshAfterDirectoryChange();
    },

    cancelPendingChannelRefreshes: (): void => {
      refreshAfterDocumentationChange.cancel();
      refreshAfterDirectoryChange.cancel();
      refreshAfterUpload.cancel();
    },
  };
};
